import { useTranslation } from "react-i18next";

import { cn } from "../../lib/utils.js";
import { COUNTER_ICON, COUNTER_TEXT, type CounterSize } from "./counter.js";

/**
 * How many cards a side holds, said with a card rather than with the word.
 *
 * # Why it exists at all
 *
 * The collapsed side row is the one surface built to be read at a glance, and it
 * said *"3 cards"* in words next to a coin drawn as a coin. Two counts, two
 * grammars. *"For cards can we use an icon too."*
 *
 * ⚠️ **The number, then the mark — the same order as
 * [`CoinPurse`](./CoinPurse.js), and it is the maintainer's.** The sketch said
 * *"3 {card icon} 4 {coin icon}"* and it was confirmed against the built screens:
 * *"the coin icon should be always to the right."* The two orders cannot both be
 * right in one row — a reader scanning it should meet the same shape twice — so
 * both counters flipped together, including the phase-18 purse that had been
 * icon-first everywhere.
 *
 * # The word survives where it is free
 *
 * The count is spoken as *"3 cards"* — the plural is i18next's, so a language
 * whose rules differ from English's is a translation rather than a code change.
 * At 12px the icon carries it; a screen reader gets the sentence.
 */
export function CardCount({
  count,
  size = "near",
  className,
}: {
  /** How many cards are on the timeline. */
  count: number;
  /** How much room there is, and how far away it is read from. */
  size?: CounterSize;
  className?: string;
}) {
  const { t } = useTranslation();
  const icon = COUNTER_ICON[size];

  return (
    <p
      data-testid="card-count"
      className={cn(
        "inline-flex items-center text-white/70",
        COUNTER_TEXT[size],
        className,
      )}
    >
      <span className="font-semibold tabular-nums">{count}</span>
      <span className="sr-only">{t("side.cards", { count })}</span>

      <svg
        width={icon}
        height={icon}
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
        className="shrink-0"
      >
        {/* Two cards, not one: a single rounded rectangle at 16px is a button,
            a note, or a screen. The one behind, tilted, is what makes it a
            hand. */}
        <rect
          x="6.5"
          y="3"
          width="12"
          height="16"
          rx="2.5"
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.45"
          strokeWidth="1.6"
          transform="rotate(12 12 12)"
        />
        <rect
          x="4"
          y="5"
          width="12"
          height="16"
          rx="2.5"
          fill="currentColor"
          fillOpacity="0.12"
          stroke="currentColor"
          strokeWidth="1.6"
        />
      </svg>
    </p>
  );
}
